import { useId } from 'react';
import { NumberFilter, TextFilter } from 'ag-grid-community';
import { cn, useTheme } from '@circuitry-ai/doc-ui';

/**
 * Server-paged tables keep only the current page in the client-side row model, so the grid shows
 * the previous page (under a loader) until the next one arrives. The server already sorts and
 * filters that page: these keep the grid's sort/filter UI and models but stop it re-sorting or
 * re-filtering the rows locally.
 */
class ServerSideTextFilter extends TextFilter {
    override doesFilterPass() {
        return true;
    }
}

class ServerSideNumberFilter extends NumberFilter {
    override doesFilterPass() {
        return true;
    }
}

/** Pass as the grid's `components` to override the built-in filters with server-side ones. */
const serverSideFilterComponents = {
    agTextColumnFilter: ServerSideTextFilter,
    agNumberColumnFilter: ServerSideNumberFilter,
};

/** Pass as the grid's `context`; custom filters (e.g. CustomDateFilter) then pass every row. */
const serverSideFilterContext = { serverSideFiltering: true };

/** Spread into `defaultColDef`: keeps the server's row order when a column is sorted. */
const serverSideSortColDef = {
    comparator: () => 0,
};

const DEFAULT_PAGE_SIZE_OPTIONS = [20, 40, 50];

type ServerPaginationPanelProps = Readonly<{
    /** 1-based page currently shown */
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    pageSizeOptions?: number[];
    hidePageSize?: boolean;
    hideRowSummary?: boolean;
}>;

type PagerButtonProps = Readonly<{
    icon: string;
    label: string;
    disabled: boolean;
    onClick: () => void;
}>;

type PageSizeSelectProps = Readonly<{
    value: number;
    options: number[];
    onChange: (value: number) => void;
}>;

const formatNumber = (value: number) => value.toLocaleString();

/** AG Grid's paging button classes on a native button (Tailwind's preflight strips the button's own styling) */
const PagerButton = ({ icon, label, disabled, onClick }: PagerButtonProps) => (
    <button
        type="button"
        className={`ag-button ag-paging-button ${disabled ? 'ag-disabled' : ''}`}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
    >
        <span className={`ag-icon ag-icon-${icon}`} aria-hidden="true" />
    </button>
);

/** AG Grid's page size picker look (label, bordered field, small-down icon) on a native select */
const PageSizeSelect = ({ value, options, onChange }: PageSizeSelectProps) => {
    const id = useId();
    return (
        <div className="ag-picker-field ag-labeled ag-label-align-left ag-select">
            <label className="ag-label" htmlFor={id}>Page Size:</label>
            <div className="ag-wrapper ag-picker-field-wrapper ag-picker-collapsed">
                <select
                    id={id}
                    className="ag-picker-field-display"
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    style={{ appearance: 'none', background: 'transparent', border: 0, color: 'inherit', font: 'inherit', outline: 'none', width: '100%', cursor: 'default' }}
                >
                    {options.map((size) => (
                        <option key={size} value={size} style={{ backgroundColor: 'var(--ag-background-color)', color: 'var(--ag-foreground-color)' }}>
                            {size}
                        </option>
                    ))}
                </select>
                <div className="ag-picker-field-icon" aria-hidden="true" style={{ pointerEvents: 'none' }}>
                    <span className="ag-icon ag-icon-small-down" />
                </div>
            </div>
        </div>
    );
};

/** AG Grid pagination footer (same markup and theme classes) driven by server totals.
 *  Render it inside the grid's theme wrapper. */
const ServerPaginationPanel = ({
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    hidePageSize = false,
    hideRowSummary = false,
}: ServerPaginationPanelProps) => {
    const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
    const shownPage = totalPages > 0 ? page : 0;
    const firstRow = total > 0 ? (page - 1) * pageSize + 1 : 0;
    const lastRow = Math.min(page * pageSize, total);
    const isFirstPage = page <= 1;
    const isLastPage = page >= totalPages;
    const sizeOptions = pageSizeOptions.includes(pageSize) ? pageSizeOptions : [...pageSizeOptions, pageSize].sort((a, b) => a - b);
    const {isDark} = useTheme();

    return (
        // ag-ltr: AG Grid's left-to-right spacing rules are scoped to it (the grid sets it on its own root only)
        <div className={cn ('ag-paging-panel ag-unselectable ag-ltr border', isDark ? 'border-[#252b32]' : 'border-[#e0e0e0]')}>
            {!hidePageSize && onPageSizeChange && (
                <span className="ag-paging-page-size">
                    <PageSizeSelect value={pageSize} options={sizeOptions} onChange={onPageSizeChange} />
                </span>
            )}
            {!hideRowSummary && (
                <span className="ag-paging-row-summary-panel" aria-live="polite">
                    <span className="ag-paging-row-summary-panel-number">{formatNumber(firstRow)}</span>
                    {' '}<span>to</span>{' '}
                    <span className="ag-paging-row-summary-panel-number">{formatNumber(lastRow)}</span>
                    {' '}<span>of</span>{' '}
                    <span className="ag-paging-row-summary-panel-number">{formatNumber(total)}</span>
                </span>
            )}
            <span className="ag-paging-page-summary-panel">
                <PagerButton icon="first" label="First Page" disabled={isFirstPage} onClick={() => onPageChange(1)} />
                <PagerButton icon="previous" label="Previous Page" disabled={isFirstPage} onClick={() => onPageChange(page - 1)} />
                <span className="ag-paging-description" aria-live="polite">
                    <span>Page</span>{' '}
                    <span className="ag-paging-number">{formatNumber(shownPage)}</span>
                    {' '}<span>of</span>{' '}
                    <span className="ag-paging-number">{formatNumber(totalPages)}</span>
                </span>
                <PagerButton icon="next" label="Next Page" disabled={isLastPage} onClick={() => onPageChange(page + 1)} />
                <PagerButton icon="last" label="Last Page" disabled={isLastPage} onClick={() => onPageChange(totalPages)} />
            </span>
        </div>
    );
};

export type { ServerPaginationPanelProps };
export { ServerPaginationPanel, serverSideFilterComponents, serverSideFilterContext, serverSideSortColDef };
